import type {
  AiDecisionFeedItem,
  BacktestRequestBody,
  BacktestResponse,
  ConfidenceBucketDto,
  DailyPnlDto,
  MemeOverviewDto,
  NotificationConfigPatch,
  NotificationConfigView,
  NotificationLogEntry,
  NotificationTestBody,
  NotificationTestResult,
  OverviewDto,
  SecretStatusMap,
  PositionsDto,
  SessionStatus,
  SettingsMap,
  SettingsResponse,
  SignalsDto,
  SymbolsResponse,
  TechnicalErrorDto,
  TuningSuggestionResponse,
} from "@/api/types";
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8787";
export class ApiError extends Error {
  status: number;
  details: Array<{ field: string; message: string }>;
  constructor(message: string, status: number, details: Array<{ field: string; message: string }> = []) {
    super(message);
    this.status = status;
    this.details = details;
  }
}
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...init.headers,
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: string;
      errors?: Array<{ field?: string; key?: string; message?: string }>;
    } | null;
    const details = (Array.isArray(body?.errors) ? body.errors : []).map((item) => ({
      field: item.field ?? item.key ?? "",
      message: item.message ?? "Invalid value",
    }));
    const detailText =
      details.length > 0
        ? `: ${details.map((item) => (item.field ? `${item.field} - ${item.message}` : item.message)).join("; ")}`
        : "";
    throw new ApiError(
      `${body?.error ?? `Request failed with status ${response.status}`}${detailText}`,
      response.status,
      details,
    );
  }
  return response.json() as Promise<T>;
}
export function fetchSession(): Promise<SessionStatus> {
  return request<SessionStatus>("/api/auth/session");
}
export function loginStep1(
  username: string,
  password: string,
): Promise<
  | { twoFactorRequired: true; pendingToken: string; expiresInSeconds: number }
  | { twoFactorRequired: false; ok: true; expiresAt: number }
> {
  return request("/api/auth/login-step1", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
}
export function loginStep2(
  pendingToken: string,
  totpCode: string,
): Promise<{
  ok: true;
  expiresAt: number;
}> {
  return request("/api/auth/login-step2", {
    method: "POST",
    body: JSON.stringify({ pendingToken, totpCode }),
  });
}
export function logout(): Promise<{
  ok: true;
}> {
  return request("/api/auth/logout", { method: "POST" });
}
export function fetchOverview(): Promise<OverviewDto> {
  return request<OverviewDto>("/api/dashboard/overview");
}
export function fetchSignals(): Promise<SignalsDto> {
  return request<SignalsDto>("/api/dashboard/signals");
}
export function fetchLivePrices(): Promise<{
  futures: Array<{
    symbol: string;
    price: number;
  }>;
}> {
  return request("/api/dashboard/live-prices");
}
export function fetchCandles(
  symbol: string,
  timeframe = "1h",
  limit = 120,
): Promise<{
  symbol: string;
  timeframe: string;
  candles: {
    openTime: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
  }[];
}> {
  const params = new URLSearchParams({ symbol, tf: timeframe, limit: String(limit) });
  return request(`/api/dashboard/candles?${params.toString()}`);
}
export function fetchPositions(): Promise<PositionsDto> {
  return request<PositionsDto>("/api/dashboard/positions");
}
export function fetchDecisions(limit = 30): Promise<{
  decisions: AiDecisionFeedItem[];
}> {
  return request(`/api/dashboard/decisions?limit=${limit}`);
}
export function fetchPnlHistory(days = 14): Promise<{
  history: DailyPnlDto[];
}> {
  return request(`/api/dashboard/pnl-history?days=${days}`);
}
export function fetchErrors(): Promise<{
  errors: TechnicalErrorDto[];
}> {
  return request("/api/dashboard/errors");
}
export function fetchLogs(limit = 200): Promise<{
  logs: TechnicalErrorDto[];
}> {
  return request(`/api/dashboard/logs?limit=${limit}`);
}
export function fetchSettings(): Promise<SettingsResponse> {
  return request<SettingsResponse>("/api/settings");
}
export interface SettingsConfirmation {
  password: string;
  totpCode?: string;
}
export function updateSettings(
  patch: Record<string, SettingsMap[string] | null>,
  confirmation?: SettingsConfirmation,
): Promise<{ settings: SettingsMap; secrets: SecretStatusMap }> {
  return request("/api/settings", {
    method: "PATCH",
    body: JSON.stringify(confirmation ? { ...patch, _confirm: confirmation } : patch),
  });
}
export function runNow(): Promise<{ started: boolean; startedAt?: number; error?: string }> {
  return request("/api/dashboard/run-now", { method: "POST" });
}
export function fetchRunStatus(): Promise<{
  isRunning: boolean;
  triggerSource: string | null;
  acquiredAt: number | null;
}> {
  return request("/api/dashboard/run-status");
}
export function fetchSymbols(): Promise<SymbolsResponse> {
  return request<SymbolsResponse>("/api/symbols");
}
export interface ManualCloseResult {
  ok: boolean;
  tradeId: string;
  symbol?: string;
  realizedPnlUsd?: number;
  errorMessage?: string;
}
export function closePosition(tradeId: string): Promise<ManualCloseResult> {
  return request(`/api/dashboard/positions/${tradeId}/close`, { method: "POST" });
}
export function closeAllPositions(): Promise<{ results: ManualCloseResult[]; tradingPaused?: boolean }> {
  return request("/api/dashboard/positions/close-all", { method: "POST" });
}
export function cancelPendingOrders(tradeId: string): Promise<{ ok: boolean; tradeId: string; errorMessage?: string }> {
  return request(`/api/dashboard/positions/${tradeId}/cancel-orders`, { method: "POST" });
}
export function changeUsername(currentPassword: string, newUsername: string): Promise<{ ok: true }> {
  return request("/api/auth/security/username", {
    method: "POST",
    body: JSON.stringify({ currentPassword, newUsername }),
  });
}
export function changePassword(currentPassword: string, newPassword: string): Promise<{ ok: true }> {
  return request("/api/auth/security/password", {
    method: "POST",
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}
export function startTotpSetup(currentPassword: string): Promise<{
  setupToken: string;
  secret: string;
  provisioningUri: string;
  expiresInSeconds: number;
}> {
  return request("/api/auth/security/2fa/setup", {
    method: "POST",
    body: JSON.stringify({ currentPassword }),
  });
}
export function confirmTotpSetup(setupToken: string, totpCode: string): Promise<{ ok: true }> {
  return request("/api/auth/security/2fa/confirm", {
    method: "POST",
    body: JSON.stringify({ setupToken, totpCode }),
  });
}
export function disableTotp(currentPassword: string, totpCode: string): Promise<{ ok: true }> {
  return request("/api/auth/security/2fa/disable", {
    method: "POST",
    body: JSON.stringify({ currentPassword, totpCode }),
  });
}
export function runBacktest(body: Partial<BacktestRequestBody>): Promise<BacktestResponse> {
  return request<BacktestResponse>("/api/backtest", {
    method: "POST",
    body: JSON.stringify(body),
  });
}
export function fetchConfidenceCalibration(): Promise<{ calibration: ConfidenceBucketDto[] }> {
  return request("/api/dashboard/confidence-calibration");
}
export function fetchMemeOverview(): Promise<MemeOverviewDto> {
  return request<MemeOverviewDto>("/api/dashboard/meme");
}
export function fetchTuningSuggestion(): Promise<TuningSuggestionResponse> {
  return request<TuningSuggestionResponse>("/api/backtest/tuning-suggestion");
}
export function fetchNotificationConfig(): Promise<{ config: NotificationConfigView }> {
  return request("/api/notifications/config");
}
export function updateNotificationConfig(patch: NotificationConfigPatch): Promise<{ config: NotificationConfigView }> {
  return request("/api/notifications/config", {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}
export function sendNotificationTest(body: NotificationTestBody): Promise<NotificationTestResult> {
  return request("/api/notifications/test", {
    method: "POST",
    body: JSON.stringify(body),
  });
}
export function fetchNotificationLog(limit = 30): Promise<{ events: NotificationLogEntry[] }> {
  return request(`/api/notifications/log?limit=${limit}`);
}
